/**
 * AI RedTeam | Stress Test
 * Single input box + sector selector → 3 simultaneous AI critics → critiques + mitigations
 * Accessible to Director (super_admin/admin) and Controllers only.
 */

import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { openPrintWindow } from "@/lib/redteam-print";
import { toast } from "sonner";
import {
  TrendingDown,
  Users,
  Swords,
  ShieldCheck,
  Loader2,
  Lightbulb,
  AlertTriangle,
  Printer,
  Tag,
} from "lucide-react";

// ─── Sector / client-type options ─────────────────────────────────────────────
const SECTORS = [
  { value: "general",          label: "General (no specific sector)" },
  { value: "restaurant",       label: "Restaurant" },
  { value: "bodega",           label: "Bodega / Small Market" },
  { value: "trucking",         label: "Transportation & Trucking" },
  { value: "automotive",       label: "Automotive Repair Shop" },
  { value: "real_estate",      label: "Real Estate Investor" },
  { value: "contractor",       label: "General Contractor" },
  { value: "internal",         label: "Internal / APX Operations" },
] as const;

type SectorValue = (typeof SECTORS)[number]["value"];

// ─── Critic config ────────────────────────────────────────────────────────────
const CRITICS = [
  {
    key: "cfoCritique" as const,
    label: "CFO Skeptic",
    subtitle: "Unit economics & financial risk",
    icon: TrendingDown,
    color: "text-red-500",
    bg: "bg-red-50 dark:bg-red-950/30",
    border: "border-red-200 dark:border-red-800",
    badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
  {
    key: "clientCritique" as const,
    label: "Resistant Client",
    subtitle: "Price-sensitive LA small business owner",
    icon: Users,
    color: "text-amber-500",
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-800",
    badgeClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  {
    key: "competitorCritique" as const,
    label: "Competitor",
    subtitle: "Local CPA firm or national platform",
    icon: Swords,
    color: "text-purple-500",
    bg: "bg-purple-50 dark:bg-purple-950/30",
    border: "border-purple-200 dark:border-purple-800",
    badgeClass: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  },
] as const;

// ─── Parse critique text into objections + mitigation ─────────────────────────
function parseCritique(text: string): { objections: string; mitigation: string } {
  const mitigationIdx = text.toLowerCase().indexOf("mitigation:");
  if (mitigationIdx === -1) {
    return { objections: text.trim(), mitigation: "" };
  }
  return {
    objections: text.slice(0, mitigationIdx).trim(),
    mitigation: text.slice(mitigationIdx + "mitigation:".length).trim(),
  };
}

// ─── Critic Panel ─────────────────────────────────────────────────────────────
function CriticPanel({
  critic,
  text,
}: {
  critic: (typeof CRITICS)[number];
  text: string;
}) {
  const Icon = critic.icon;
  const { objections, mitigation } = parseCritique(text);

  return (
    <Card className={`border ${critic.border} ${critic.bg} shadow-sm`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Icon className={`w-5 h-5 ${critic.color}`} />
          <span>{critic.label}</span>
          <Badge className={`ml-auto text-xs font-normal ${critic.badgeClass}`}>
            {critic.subtitle}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Objections */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Objections
          </p>
          <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground">
            {objections}
          </div>
        </div>

        {/* Mitigation */}
        {mitigation && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-emerald-500" />
                Mitigation
              </p>
              <div className="text-sm leading-relaxed text-emerald-700 dark:text-emerald-400 font-medium whitespace-pre-wrap">
                {mitigation}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Delegate all print/PDF logic to the dedicated helper module.
function printReport(result: {
  idea: string;
  sector: string;
  cfoCritique: string;
  clientCritique: string;
  competitorCritique: string;
  thirdCriticLabel?: string;
  thirdCriticSubtitle?: string;
}) {
  openPrintWindow(result);
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function RedTeam() {
  const { user } = useAuth();
  const [idea, setIdea] = useState("");
  const [sector, setSector] = useState<SectorValue>("general");
  const [result, setResult] = useState<{
    idea: string;
    sector: string;
    cfoCritique: string;
    clientCritique: string;
    competitorCritique: string;
    thirdCriticLabel?: string;
    thirdCriticSubtitle?: string;
  } | null>(null);

  const redTeamMutation = trpc.intelligence.redTeam.useMutation({
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (err) => {
      const msg = err.message ?? '';
      if (msg.toLowerCase().includes('rate') || msg.toLowerCase().includes('429') || msg.toLowerCase().includes('exceeded')) {
        toast.error('The AI service is temporarily rate-limited. Please wait 30 seconds and try again.');
      } else if (msg.toLowerCase().includes('overloaded') || msg.toLowerCase().includes('529')) {
        toast.error('The AI service is temporarily overloaded. Please try again in a moment.');
      } else {
        toast.error('Red Team analysis failed. Please try again.');
      }
    },
  });

  const handleSubmit = () => {
    if (!idea.trim()) {
      toast.error("Please enter an idea or decision to stress-test.");
      return;
    }
    setResult(null);
    redTeamMutation.mutate({
      idea: idea.trim(),
      sector,
      callerEmail: user?.email,
      userName: user?.name,
    });
  };

  const handleClear = () => {
    setIdea("");
    setSector("general");
    setResult(null);
  };

  const sectorLabel = SECTORS.find(s => s.value === sector)?.label ?? "General";

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">AI RedTeam | Stress Test</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Type a business idea or decision. Three AI critics challenge it simultaneously — each with a specific objection and a mitigation recommendation.
            </p>
          </div>
        </div>

        {/* ── Input Box ── */}
        <Card className="shadow-sm">
          <CardContent className="pt-5 space-y-4">
            {/* Sector selector row */}
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Client / Sector
              </span>
              <Select value={sector} onValueChange={(v) => setSector(v as SectorValue)}>
                <SelectTrigger className="h-8 text-sm w-64">
                  <SelectValue placeholder="Select sector…" />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sector !== "general" && (
                <Badge variant="secondary" className="text-xs">
                  AI tailored to: {sectorLabel}
                </Badge>
              )}
            </div>

            {/* Idea textarea */}
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500 mt-3 shrink-0" />
              <Textarea
                placeholder="Describe your idea or decision in plain language."
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                rows={5}
                className="resize-none text-sm"
                maxLength={2000}
              />
            </div>

            {/* Selectable examples */}
            <div className="ml-6 rounded border border-dashed border-muted-foreground/30 bg-muted/40 px-3 py-2 space-y-0.5 select-text cursor-text">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Examples — click to copy</p>
              {[
                { text: 'A major food delivery platform raises its commission rate to 35% — how does this affect restaurant cash flow and our fee justification?', sector: 'restaurant' as SectorValue },
                { text: 'A regional recession cuts dining out by 20% — model which client segments go dark first and how APX retains them.', sector: 'general' as SectorValue },
                { text: 'A large grocery chain opens two locations within a mile of three market/bodega clients — stress test client revenue assumptions.', sector: 'bodega' as SectorValue },
                { text: 'A shop owner retires with no succession plan — how does APX identify at-risk clients early and what is the advisory play?', sector: 'general' as SectorValue },
                { text: 'Hire another Bookkeeper Manager to handle the work overflow.', sector: 'internal' as SectorValue },
              ].map((ex) => (
                <p
                  key={ex.text}
                  className="text-[11px] text-muted-foreground leading-snug cursor-pointer hover:text-foreground hover:bg-muted rounded px-1 py-0.5 transition-colors select-text"
                  onClick={() => {
                    setIdea(ex.text);
                    setSector(ex.sector);
                    const label = SECTORS.find(s => s.value === ex.sector)?.label ?? 'General';
                    toast.success('Example loaded' + (ex.sector !== 'general' ? ` · Sector set to ${label}` : ''));
                  }}
                  title="Click to load into input"
                >
                  • {ex.text}
                </p>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{idea.length}/2000 characters</span>
              <div className="flex gap-2">
                {(result || idea) && (
                  <Button variant="outline" size="sm" onClick={handleClear}>
                    Clear
                  </Button>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={redTeamMutation.isPending || !idea.trim()}
                  size="sm"
                  className="min-w-[140px]"
                >
                  {redTeamMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running analysis…
                    </>
                  ) : (
                    "Run Red Team Analysis"
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Loading state ── */}
        {redTeamMutation.isPending && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {CRITICS.map((c) => (
              <Card key={c.key} className={`border ${c.border} ${c.bg} animate-pulse`}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <c.icon className={`w-5 h-5 ${c.color}`} />
                    {c.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-current opacity-10 rounded w-full" />
                    <div className="h-3 bg-current opacity-10 rounded w-5/6" />
                    <div className="h-3 bg-current opacity-10 rounded w-4/6" />
                    <div className="h-3 bg-current opacity-10 rounded w-full" />
                    <div className="h-3 bg-current opacity-10 rounded w-3/4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ── Results ── */}
        {result && !redTeamMutation.isPending && (
          <div className="space-y-4" id="redteam-results">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground shrink-0">
                  Stress-testing:
                </p>
                <p className="text-sm font-medium text-foreground italic truncate max-w-xl">
                  "{result.idea}"
                </p>
                {result.sector && result.sector !== "general" && (
                  <Badge variant="secondary" className="text-xs shrink-0">
                    <Tag className="w-3 h-3 mr-1" />
                    {SECTORS.find(s => s.value === result.sector)?.label ?? result.sector}
                  </Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="print:hidden shrink-0 gap-1.5"
                onClick={() => result && printReport(result)}
              >
                <Printer className="w-4 h-4" />
                Print / Save as PDF
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {CRITICS.map((critic) => {
                // For the third critic, override label/subtitle with server-provided context-aware values
                const overriddenCritic = (critic.key === 'competitorCritique' && result.thirdCriticLabel
                  ? { ...critic, label: result.thirdCriticLabel, subtitle: result.thirdCriticSubtitle ?? critic.subtitle }
                  : critic) as (typeof CRITICS)[number];
                return (
                  <CriticPanel
                    key={critic.key}
                    critic={overriddenCritic}
                    text={result[critic.key]}
                  />
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground text-center pt-2">
              AI RedTeam | Stress Test · APX Internal Use Only · Results are AI-generated and should be reviewed critically before acting on them.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
